import { PowerOff } from 'lucide-react'
import { useState } from 'react'

import { Button } from '#components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '#components/ui/dialog'
import { useToast } from '#hooks/use-toast'
import { trpc } from '#lib/trpc'

export function AccountClosureModal({
  id,
  name,
  onClosed,
  self = false,
  label,
}: {
  id: string
  name?: string
  onClosed: () => void
  self?: boolean
  label?: string
}) {
  const [open, setOpen] = useState(false)
  const { toast } = useToast()
  const utils = trpc.useUtils()
  const mutation = trpc.user.delete.useMutation({
    onSettled: () => {
      void utils.adminUser.invalidate()
      void utils.user.getAll.invalidate()
    },
    onSuccess: (result) => {
      setOpen(false)
      toast({
        title: 'Compte désactivé',
        description: result.warnings.length
          ? result.warnings.join(' ')
          : 'Les abonnements et réservations à venir ont été annulés.',
      })
      onClosed()
    },
  })
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (mutation.isPending) return
        setOpen(value)
        if (value) mutation.reset()
      }}
    >
      <DialogTrigger asChild>
        {self ? (
          <Button variant="outlineDestructive" className="mt-4">
            Désactiver mon compte
          </Button>
        ) : (
          <Button
            variant={label ? 'destructive' : 'none'}
            size={label ? 'default' : 'icon'}
            aria-label={label ?? `Désactiver le compte de ${name ?? ''}`}
          >
            <PowerOff className="size-4 text-red-600" />
            {label}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="flex max-h-[90dvh] max-w-lg flex-col gap-5 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {self ? 'Désactiver mon compte' : `Désactiver le compte de ${name}`}
          </DialogTitle>
          <DialogDescription>
            Cette action clôture le compte et bloque immédiatement son accès.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p>
            Les abonnements seront arrêtés immédiatement, sans nouvelle facturation. Les cours à
            venir seront annulés et les jetons réservés seront restitués au solde conservé du
            compte.
          </p>
          <p>
            Pour un parent, cela concerne aussi les réservations de ses enfants couverts. Pour un
            enfant, les autres enfants conservent leur couverture et la quantité facturée est
            ajustée.
          </p>
          <p>
            Les paiements passés ne sont pas automatiquement remboursés. Les séances passées et les
            documents comptables sont conservés. La désactivation n’est pas une demande d’effacement
            des données ; contactez le support pour exercer ce droit.
          </p>
          <p>
            En cas d’échec, l’accès reste bloqué et un administrateur doit terminer les annulations.
          </p>
        </div>
        {mutation.error && (
          <p role="alert" className="text-sm text-red-600">
            {mutation.error.message}
          </p>
        )}
        <DialogFooter className="gap-3">
          <DialogClose asChild>
            <Button variant="outline" disabled={mutation.isPending}>
              Annuler
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => {
              mutation.mutate(id)
            }}
          >
            {mutation.isPending ? 'Clôture en cours…' : 'Confirmer la désactivation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
